import React, { useState, useEffect } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaEdit, FaPlus, FaArrowLeft, FaSearch, FaTrash, FaTags } from 'react-icons/fa';
import { showSuccessToast, showErrorToast } from '../../../shared/utils/toastHelper';
import ConfirmModal from '../../../shared/components/ConfirmModal';
import apiClient from '../../../shared/utils/apiConfig';

const AdminSubSubCategories = () => {
    const { setTitle } = useTitle();
    const { subCategoryId } = useParams();
    const navigate = useNavigate();
    
    const [subSubCategories, setSubSubCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, data: null });

    useEffect(() => {
        setTitle('إدارة الفئات الفرعية للفئات الفرعية');
    }, [setTitle]);

    useEffect(() => {
        // جلب Sub-Sub Categories للفئة المحددة
        if (subCategoryId) {
            fetchSubSubCategories();
        }
    }, [subCategoryId, searchTerm]);

    const fetchSubSubCategories = async () => {
        try {
            setLoading(true);
            
            let url = `/admin/sub_category/${subCategoryId}/sub_sub_categories`;
            if (searchTerm.trim()) {
                url += `?search=${encodeURIComponent(searchTerm.trim())}`;
            }

            const response = await apiClient.get(url);

            if (response.data.status === 'success' || response.data.statusCode === 200) {
                setSubSubCategories(response.data.result || []);
            } else {
                throw new Error(response.data.message || 'فشل في جلب الفئات الفرعية');
            }
        } catch (error) {
            showErrorToast(error.response?.data?.message || error.message || 'فشل في جلب الفئات الفرعية');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (subSubCategoryId) => {
        try {
            await apiClient.delete(`/admin/sub_category/sub-sub-categories/${subSubCategoryId}`);
            showSuccessToast('تم حذف الفئة الفرعية بنجاح');
            fetchSubSubCategories();
        } catch (error) {
            showErrorToast('فشل في حذف الفئة الفرعية');
        }
    };

    const handleEdit = (subSubCategoryId) => {
        navigate(`/admin-categories/${subCategoryId}/sub-sub-categories/edit/${subSubCategoryId}`);
    };

    const handleAddNew = () => {
        navigate(`/admin-categories/${subCategoryId}/sub-sub-categories/add`);
    };

    const handleSearch = () => {
        // البحث يتم تلقائياً عند تغيير searchTerm
    };

    const handleClearSearch = () => {
        setSearchTerm('');
    };

    const confirmDelete = (subSubCategory) => {
        setConfirmModal({
            isOpen: true,
            data: subSubCategory
        });
    };

    const executeDelete = () => {
        if (confirmModal.data) {
            handleDelete(confirmModal.data.id);
            setConfirmModal({ isOpen: false, data: null });
        }
    };

    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div className="d-flex align-items-center">
                        <button 
                            className="btn btn-outline-secondary mx-3"
                            onClick={() => navigate(-1)}>
                            <FaArrowLeft className="mx-2" />
                            العودة
                        </button>
                    </div>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="btn text-white d-flex align-items-center"
                        onClick={handleAddNew}
                        style={{ backgroundColor: 'rgb(24, 65, 148)' }}>
                        <FaPlus className="mx-2" />
                        إضافة فئة فرعية جديدة
                    </motion.button>
                </div>

                {/* Search */}
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="row g-3">
                            <div className="col-md-8">
                                <label className="form-label">البحث</label>
                                <div className="input-group">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="ابحث في الفئات الفرعية..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                    />
                                    <button 
                                        className="btn text-white" 
                                        style={{ backgroundColor: 'rgb(24, 65, 148)' }}
                                        onClick={handleSearch}>
                                        <FaSearch />
                                    </button>
                                </div>
                            </div>
                            <div className="col-md-4 d-flex align-items-end">
                                <button 
                                    className="btn btn-outline-secondary w-100"
                                    onClick={handleClearSearch}>
                                    مسح البحث
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sub-Sub Categories Table */}
                <div className="card shadow-lg border-0">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3 text-muted">جاري تحميل الفئات الفرعية...</p>
                            </div>
                        ) : !Array.isArray(subSubCategories) || subSubCategories.length === 0 ? (
                            <div className="text-center py-5">
                                <FaTags className="text-muted" style={{ fontSize: '3rem' }} />
                                <p className="mt-3 text-muted">لا توجد فئات فرعية</p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-modern">
                                    <thead>
                                        <tr>
                                            <th className="text-center">كود الفئة الفرعية</th>
                                            <th className="text-center">الاسم (عربي)</th>
                                            <th className="text-center">الاسم (إنجليزي)</th>
                                            <th className="text-center">الإجراءات</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {subSubCategories.map((subSubCategory, index) => (
                                            <tr key={subSubCategory.id} className="table-row-modern">
                                                <td className="text-center">
                                                    <div className="order-number-badge">
                                                        {subSubCategory?.id}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <div className="category-name">
                                                        {subSubCategory.name || 'غير محدد'}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <div className="category-name">
                                                        {subSubCategory.name || 'غير محدد'}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <div className="action-buttons">
                                                        <motion.button
                                                            whileHover={{ scale: 1.1, y: -2 }}
                                                            whileTap={{ scale: 0.95 }}
                                                            className="btn btn-edit mx-2"
                                                            onClick={() => handleEdit(subSubCategory.id)}
                                                            title="تعديل">
                                                            <FaEdit />
                                                        </motion.button>
                                                        <motion.button
                                                            whileHover={{ scale: 1.1, y: -2 }}
                                                            whileTap={{ scale: 0.95 }}
                                                            className="btn btn-delete"
                                                            onClick={() => confirmDelete(subSubCategory)}
                                                            title="حذف">
                                                            <FaTrash />
                                                        </motion.button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

                {/* Delete Confirmation Modal */}
                <ConfirmModal
                    show={confirmModal.isOpen}
                    onHide={() => setConfirmModal({ isOpen: false, data: null })}
                    onConfirm={executeDelete}
                    title="تأكيد الحذف"
                    message={`هل أنت متأكد من حذف الفئة الفرعية: "${confirmModal.data?.name || 'غير محدد'}"؟`}
                    confirmText="حذف"
                    cancelText="إلغاء"
                    variant="danger"
                />
            </div>
        </div>
    );
};

export default AdminSubSubCategories;
